import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { DateTime } from "luxon";
import { useMyGames, useMyProfile, useMyStatistics } from "@seabrookstudios/pitch2table";
import {
  makeGamesPerUser,
  makeGamesPerUserPerYear,
  makePitchesPerUser,
  makePitchesPerUserPerYear,
  makePlayTestsPerUser,
  makePlayTestsPerUserPerYear,
} from "@seabrookstudios/pitch2table-core";
import { formatNumber } from "../games/royalties/currency-formatting";
import ListItemIcon from "@mui/material/ListItemIcon";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";

export const Statistics = () => {
  const { data: games = [] } = useMyGames();
  const { data: stats = [], isLoading: isLoadingStats } = useMyStatistics();
  const { data: profile, isLoading: isLoadingProfile } = useMyProfile();

  const isLoading = isLoadingStats || isLoadingProfile;

  if (isLoading) {
    return (
      <Card elevation={1} square={false}>
        <Box p={2} justifyContent="center">
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  if (!profile) {
    return (
      <Card elevation={1} square={false}>
        <Box p={2} justifyContent="center">
          <span>An error occurred, please refresh the page.</span>
        </Box>
      </Card>
    );
  }

  const { year } = DateTime.now();

  const pitching = games.filter((game) => game.status === "PitchingNow").length;
  const signedGames = games.filter((game) => game.status === "Signed").length;
  const publishedGames = games.filter((game) => game.status === "Published").length;

  const playTestsYear =
    stats.find((s) => s.id === makePlayTestsPerUserPerYear(profile.id, DateTime.now().toMillis()))?.amount ?? 0;
  const playTests = stats.find((s) => s.id === makePlayTestsPerUser(profile.id))?.amount ?? 0;
  const totalPitches = stats.find((s) => s.id === makePitchesPerUser(profile.id))?.amount ?? 0;
  const totalPitchesYear =
    stats.find((s) => s.id === makePitchesPerUserPerYear(profile.id, DateTime.now().toMillis()))?.amount ?? 0;

  const totalGamesYear =
    stats.find((s) => s.id === makeGamesPerUserPerYear(profile.id, DateTime.now().toMillis()))?.amount ?? 0;
  const totalGames = stats.find((s) => s.id === makeGamesPerUser(profile.id))?.amount ?? 0;

  const done = signedGames + publishedGames;
  const pitchesPerSign = done > 0 ? totalPitches / done : 0;
  const pitchStrikeRate = pitching + done > 0 ? (done / (pitching + done)) * 100 : 0;

  return (
    <Card elevation={1} square={false}>
      <List>
        <ListItem>
          <ListItemText primary={`${year} Games Started`} />
          <ListItemIcon sx={{ justifyContent: "flex-end" }}>
            <Typography variant="h6">{formatNumber(totalGamesYear)}</Typography>
          </ListItemIcon>
        </ListItem>
        <ListItem>
          <ListItemText primary={`${year} Pitches`} />
          <ListItemIcon sx={{ justifyContent: "flex-end" }}>
            <Typography variant="h6">{formatNumber(totalPitchesYear)}</Typography>
          </ListItemIcon>
        </ListItem>
        <ListItem>
          <ListItemText primary={`${year} Play Tests`} />
          <ListItemIcon sx={{ justifyContent: "flex-end" }}>
            <Typography variant="h6">{formatNumber(playTestsYear)}</Typography>
          </ListItemIcon>
        </ListItem>
        <Divider />
        <ListItem>
          <ListItemText primary="Total Pitches" />
          <ListItemIcon sx={{ justifyContent: "flex-end" }}>
            <Typography variant="h6">{formatNumber(totalPitches)}</Typography>
          </ListItemIcon>
        </ListItem>
        <ListItem>
          <ListItemText primary="Total Games" />
          <ListItemIcon sx={{ justifyContent: "flex-end" }}>
            <Typography variant="h6">{formatNumber(totalGames)}</Typography>
          </ListItemIcon>
        </ListItem>
        <ListItem>
          <ListItemText primary="Total Play Tests" />
          <ListItemIcon sx={{ justifyContent: "flex-end" }}>
            <Typography variant="h6">{formatNumber(playTests)}</Typography>
          </ListItemIcon>
        </ListItem>
        <Divider />
        <ListItem>
          <ListItemText primary="Pitch Average" secondary="The average number of pitches to get a game signed." />
          <ListItemIcon sx={{ justifyContent: "flex-end" }}>
            <Typography variant="h6">{formatNumber(pitchesPerSign)}</Typography>
          </ListItemIcon>
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Pitch Strike Rate"
            secondary="The percentage of games that you have pitched that have been signed."
          />
          <ListItemIcon sx={{ justifyContent: "flex-end" }}>
            <Typography variant="h6">{formatNumber(pitchStrikeRate)}</Typography>
          </ListItemIcon>
        </ListItem>
      </List>
    </Card>
  );
};
